import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useQpmCalculationListQuery } from "@/services/backend/qpm/calculations/api/calculation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { NavLink } from "react-router";
import { Button } from "@/shared/components/ui/button";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { Copy, Loader2, Search } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import {
  useQpmOfferApplyCalculationMutation,
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferCreateAndApplyCalculationMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  ApplyCalculationDataRequest,
  CreateAndApplyCalculationDataRequest,
  CreateOfferDocumentProfile,
} from "@/services/backend/qpm/offers/types/offer";
import { useEffect } from "react";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { v4 } from "uuid";
import { Calculation } from "@/services/backend/qpm/calculations/types/calculation";
import {
  buildCreateOfferDocumentProfile,
  createDefaultOfferDocumentProfileDraft,
  createPatchFromCreateProfile,
} from "@/routes/processes/[processId]/qpm/offers/_components/offer-document-profile-form";

export function CreateFromCalculation({
  offerId,
  processId,
  documentProfile,
  isDocumentProfileValid = true,
  onCreated,
}: {
  offerId?: string;
  processId: string;
  documentProfile?: CreateOfferDocumentProfile;
  isDocumentProfileValid?: boolean;
  onCreated?: (offerId: string) => void;
}) {
  const selectedDocumentProfile =
    documentProfile ??
    buildCreateOfferDocumentProfile(createDefaultOfferDocumentProfileDraft());

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Kalkulation")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten der alten Kalkulation. Es werden nur Kalkulationen von abgeschlossenen Vorgängen angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CalculationsTable
          offerId={offerId}
          processId={processId}
          documentProfile={selectedDocumentProfile}
          isDocumentProfileValid={isDocumentProfileValid}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function CalculationsTable({
  offerId,
  processId,
  documentProfile,
  isDocumentProfileValid,
  onCreated,
}: {
  offerId?: string;
  processId: string;
  documentProfile: CreateOfferDocumentProfile;
  isDocumentProfileValid: boolean;
  onCreated?: (offerId: string) => void;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useQpmCalculationListQuery({
    process: {
      active: true,
      values: [processId],
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">{t("Aktuelle")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead className="w-56">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead className="w-56">{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...(list?.items ?? [])].sort(byCreatedAt).map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>
              <DocumentNumberById documentId={calculation.id} />
            </TableCell>
            <TableCell>
              {new Date(calculation.createdAt).toLocaleDateString()}
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink to={`../calculations/${calculation.id}`}>
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
                {offerId && (
                  <ApplyCalculationButton
                    calculationId={calculation.id}
                    customerId={calculation.customerId}
                    processId={processId}
                    onCreated={onCreated}
                    offerId={offerId}
                  />
                )}
                {!offerId && (
                  <CreateOfferFromCalculationButton
                    calculationId={calculation.id}
                    customerId={calculation.customerId}
                    processId={processId}
                    documentProfile={documentProfile}
                    isDocumentProfileValid={isDocumentProfileValid}
                    onCreated={onCreated}
                  />
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function byCreatedAt(a: Calculation, b: Calculation) {
  return a.createdAt.localeCompare(b.createdAt);
}

export type CreateFromCalculationButtonProps = {
  calculationId: string;
  customerId: string;
  processId: string;
  documentProfile: CreateOfferDocumentProfile;
  isDocumentProfileValid: boolean;
  onCreated?: (offerId: string) => void;
};

function CreateOfferFromCalculationButton({
  calculationId,
  customerId,
  processId,
  documentProfile,
  isDocumentProfileValid,
  onCreated,
}: CreateFromCalculationButtonProps) {
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [createAndApply, { error, isLoading, reset }] =
    useQpmOfferCreateAndApplyCalculationMutation();
  const [
    changeDocumentProfile,
    { error: patchError, isLoading: isPatchLoading, reset: resetPatch },
  ] = useQpmOfferChangeDocumentProfileMutation();
  const [
    reloadSnapshot,
    { error: reloadError, isLoading: isReloadLoading, reset: resetReload },
  ] = useQpmOfferReloadOfferConfigSnapshotMutation();

  const onClick = async () => {
    if (
      !isDocumentProfileValid ||
      isLoading ||
      isPatchLoading ||
      isReloadLoading
    ) {
      return;
    }

    const offerRequestId: string = v4();
    const request: CreateAndApplyCalculationDataRequest = {
      offerId: offerRequestId,
      processId,
      customerId,
      calculationId,
      companyId: process?.companyId ?? "",
      branchId: process?.branches[0] ?? "",
    };

    try {
      const createdOffer = await createAndApply(request).unwrap();
      const createdOfferId = createdOffer.id || offerRequestId;

      await changeDocumentProfile({
        offerId: createdOfferId,
        documentProfile: createPatchFromCreateProfile(documentProfile),
      }).unwrap();

      await reloadSnapshot({ offerId: createdOfferId }).unwrap();

      if (onCreated) {
        onCreated(createdOfferId);
      }
    } catch {
      // handled by RTK error state
    }
  };

  const { toast } = useToast();

  useEffect(() => {
    const mutationError = error ?? patchError ?? reloadError;

    if (mutationError) {
      toast({ ...parseRTKQueryError(mutationError), variant: "destructive" });
      reset();
      resetPatch();
      resetReload();
    }
  }, [error, patchError, reloadError, toast, reset, resetPatch, resetReload]);

  return (
    <Button
      onClick={onClick}
      disabled={
        isLoading ||
        isPatchLoading ||
        isReloadLoading ||
        !isDocumentProfileValid
      }
      size="sm"
    >
      {(isLoading || isPatchLoading || isReloadLoading) && (
        <Loader2 className="h-4 w-4 animate-spin" />
      )}
      {!isLoading && !isPatchLoading && !isReloadLoading && (
        <>
          <Copy className="mr-3 h-4 w-4" />
          <span>{t("Daten Übernehmen")}</span>
        </>
      )}
    </Button>
  );
}

export type ApplyCalculationButtonProps = {
  calculationId: string;
  customerId: string;
  processId: string;
  offerId: string;
  onCreated?: (offerId: string) => void;
};

function ApplyCalculationButton({
  calculationId,
  customerId,
  processId,
  onCreated,
  offerId,
}: ApplyCalculationButtonProps) {
  const [createAndApply, { error, isLoading, reset }] =
    useQpmOfferApplyCalculationMutation();

  const onClick = () => {
    const request: ApplyCalculationDataRequest = {
      offerId,
      processId,
      customerId,
      calculationId,
    };

    createAndApply(request)
      .unwrap()
      .then(() => {
        if (onCreated) {
          onCreated(offerId);
        }
      });
  };
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, toast, reset]);

  return (
    <Button onClick={onClick} disabled={isLoading} size="sm">
      {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {!isLoading && (
        <>
          <Copy className="mr-3 h-4 w-4" />
          <span>{t("Daten Übernehmen")}</span>
        </>
      )}
    </Button>
  );
}
